import { useEffect, useRef } from "react";

/**
 * Global barcode-scanner listener. Wired at the POS page level so any
 * scan on the terminal — regardless of what UI is focused — resolves
 * to a menu item and gets pushed onto the current ticket.
 *
 * Detection heuristic (standard USB HID scanners emit a fast burst of
 * keystrokes ending in Enter/Tab):
 *   1. Buffer keydown chars from ``document``.
 *   2. If the buffer receives 4+ chars in under ~50ms/char, treat it
 *      as a scan. This ignores normal human typing.
 *   3. On Enter (or a 100 ms idle window), flush ``onScan(buffer)``.
 *
 * The listener respects "focus in an input/textarea" — we don't want
 * scans hijacked by a form field. If the caller explicitly wants the
 * "scan-in-dialog" flow, they can pass ``paused={true}`` while their
 * dialog owns the focus.
 */
const SCAN_INTERKEY_MS = 50;      // scanners are ~1–3 ms/char; humans > 100 ms
const SCAN_MIN_LEN = 4;           // ignore incidental keystrokes
const SCAN_FLUSH_IDLE_MS = 120;

const useBarcodeScanner = (onScan, { paused = false } = {}) => {
  const bufRef = useRef("");
  const lastKeyAtRef = useRef(0);
  const flushTimerRef = useRef(null);

  useEffect(() => {
    if (paused) return undefined;

    const isTypableField = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
    };

    const flush = () => {
      const code = bufRef.current;
      bufRef.current = "";
      if (code.length >= SCAN_MIN_LEN) {
        onScan(code);
      }
    };

    const handleKeyDown = (e) => {
      // Never hijack in-progress human typing.
      if (isTypableField()) return;

      const now = performance.now();
      const gap = now - lastKeyAtRef.current;
      lastKeyAtRef.current = now;

      // Reset buffer if a human-typing pause has elapsed
      if (gap > SCAN_INTERKEY_MS * 4) {
        bufRef.current = "";
      }

      // Terminator: Enter or Tab from the scanner
      if (e.key === "Enter" || e.key === "Tab") {
        if (bufRef.current.length >= SCAN_MIN_LEN) {
          e.preventDefault();
          flush();
        }
        return;
      }

      // Single printable char
      if (e.key.length === 1) {
        bufRef.current += e.key;
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
        flushTimerRef.current = setTimeout(flush, SCAN_FLUSH_IDLE_MS);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, [onScan, paused]);
};

export default useBarcodeScanner;
